// src/pages/kanban/components/TableroFormModal.tsx
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
  Textarea,
} from '@/components/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { SwitchWithLabel } from '@/components/ui/Switch';
import {
  useCreateTablero,
  useUpdateTablero,
} from '@/hooks/queries/useKanban';
import {
  TipoTableroKanban,
  AgrupacionKanban,
  OrdenKanban,
} from '@/types/kanban';
import type { Tablero } from '@/types/kanban';

const TIPO_LABELS: Record<TipoTableroKanban, string> = {
  [TipoTableroKanban.TICKETS]: 'Tickets',
  [TipoTableroKanban.ACTIVIDADES]: 'Actividades',
  [TipoTableroKanban.SPRINT]: 'Sprint',
  [TipoTableroKanban.PERSONALIZADO]: 'Personalizado',
};

const AGRUPACION_LABELS: Record<AgrupacionKanban, string> = {
  [AgrupacionKanban.ESTADO]: 'Estado',
  [AgrupacionKanban.PRIORIDAD]: 'Prioridad',
  [AgrupacionKanban.ASIGNADO]: 'Asignado',
  [AgrupacionKanban.CLIENTE]: 'Cliente',
  [AgrupacionKanban.TIPO]: 'Tipo',
  [AgrupacionKanban.CATEGORIA]: 'Categoria',
  [AgrupacionKanban.MODULO_ERP]: 'Modulo ERP',
};

const ORDEN_LABELS: Record<OrdenKanban, string> = {
  [OrdenKanban.MANUAL]: 'Manual',
  [OrdenKanban.PRIORIDAD]: 'Prioridad',
  [OrdenKanban.FECHA_CREACION]: 'Fecha de creación',
  [OrdenKanban.FECHA_LIMITE]: 'Fecha límite',
  [OrdenKanban.ULTIMO_MOVIMIENTO]: 'Último movimiento',
};

const TAMANO_LABELS: Record<string, string> = {
  compacto: 'Compacto',
  normal: 'Normal',
  expandido: 'Expandido',
};

const schema = z.object({
  nombre: z.string().min(1, 'Nombre requerido').max(100, 'Maximo 100 caracteres'),
  descripcion: z.string().max(500, 'Maximo 500 caracteres').optional(),
  tipo: z.nativeEnum(TipoTableroKanban),
  agrupacion: z.nativeEnum(AgrupacionKanban),
  orden_tarjetas: z.nativeEnum(OrdenKanban),
  mostrar_archivados: z.boolean(),
  es_compartido: z.boolean(),
  tamano_tarjeta: z.enum(['compacto', 'normal', 'expandido']),
});

type FormData = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onClose: () => void;
  tablero?: Tablero;
  tipoDefault?: TipoTableroKanban;
}

export function TableroFormModal({ open, onClose, tablero, tipoDefault }: Props) {
  const createMutation = useCreateTablero();
  const updateMutation = useUpdateTablero();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      nombre: '',
      descripcion: '',
      tipo: tipoDefault || TipoTableroKanban.TICKETS,
      agrupacion: AgrupacionKanban.ESTADO,
      orden_tarjetas: OrdenKanban.PRIORIDAD,
      mostrar_archivados: false,
      es_compartido: false,
      tamano_tarjeta: 'normal',
    },
  });

  const tipo = watch('tipo');
  const agrupacion = watch('agrupacion');
  const ordenTarjetas = watch('orden_tarjetas');
  const mostrarArchivados = watch('mostrar_archivados');
  const esCompartido = watch('es_compartido');
  const tamanoTarjeta = watch('tamano_tarjeta');

  useEffect(() => {
    if (tablero) {
      reset({
        nombre: tablero.nombre,
        descripcion: tablero.descripcion ?? '',
        tipo: tablero.tipo,
        agrupacion: tablero.agrupacion,
        orden_tarjetas: tablero.orden_tarjetas,
        mostrar_archivados: tablero.mostrar_archivados,
        es_compartido: tablero.es_compartido,
        tamano_tarjeta: (tablero.tamano_tarjeta as 'compacto' | 'normal' | 'expandido') || 'normal',
      });
    } else {
      reset({
        nombre: '',
        descripcion: '',
        tipo: tipoDefault || TipoTableroKanban.TICKETS,
        agrupacion: AgrupacionKanban.ESTADO,
        orden_tarjetas: OrdenKanban.PRIORIDAD,
        mostrar_archivados: false,
        es_compartido: false,
        tamano_tarjeta: 'normal',
      });
    }
  }, [tablero, tipoDefault, reset, open]);

  const onSubmit = async (data: FormData) => {
    try {
      const payload = {
        nombre: data.nombre,
        descripcion: data.descripcion || undefined,
        tipo: data.tipo,
        agrupacion: data.agrupacion,
        orden_tarjetas: data.orden_tarjetas,
        mostrar_archivados: data.mostrar_archivados,
        es_compartido: data.es_compartido,
        tamano_tarjeta: data.tamano_tarjeta,
      };

      if (tablero) {
        await updateMutation.mutateAsync({
          id: tablero.id,
          data: payload,
        });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onClose();
    } catch {
      // Error handling is done by react-query
    }
  };

  // Filtrar agrupaciones segun el tipo
  const agrupacionesDisponibles = Object.entries(AGRUPACION_LABELS).filter(([key]) => {
    if (tipo === TipoTableroKanban.TICKETS) {
      return [AgrupacionKanban.ESTADO, AgrupacionKanban.PRIORIDAD, AgrupacionKanban.ASIGNADO, AgrupacionKanban.CLIENTE, AgrupacionKanban.TIPO, AgrupacionKanban.MODULO_ERP].includes(key as AgrupacionKanban);
    }
    if (tipo === TipoTableroKanban.ACTIVIDADES) {
      return [AgrupacionKanban.ESTADO, AgrupacionKanban.PRIORIDAD, AgrupacionKanban.ASIGNADO, AgrupacionKanban.CATEGORIA, AgrupacionKanban.TIPO].includes(key as AgrupacionKanban);
    }
    return true;
  });

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {tablero ? 'Editar Tablero' : 'Nuevo Tablero Kanban'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="nombre">Nombre *</Label>
            <Input
              id="nombre"
              {...register('nombre')}
              placeholder="Mi Tablero Kanban"
              className="mt-1"
            />
            {errors.nombre && (
              <p className="text-sm text-red-500 mt-1">{errors.nombre.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="descripcion">Descripción</Label>
            <Textarea
              id="descripcion"
              {...register('descripcion')}
              placeholder="Descripción opcional del tablero..."
              rows={2}
              className="mt-1"
            />
            {errors.descripcion && (
              <p className="text-sm text-red-500 mt-1">{errors.descripcion.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="tipo">Tipo de tablero</Label>
              <Select
                value={tipo}
                onValueChange={(value) => setValue('tipo', value as TipoTableroKanban)}
                disabled={!!tablero}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TIPO_LABELS)
                    .filter(([key]) => key !== TipoTableroKanban.SPRINT && key !== TipoTableroKanban.PERSONALIZADO)
                    .map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="agrupacion">Agrupar por</Label>
              <Select
                value={agrupacion}
                onValueChange={(value) => setValue('agrupacion', value as AgrupacionKanban)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccionar agrupacion" />
                </SelectTrigger>
                <SelectContent>
                  {agrupacionesDisponibles.map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="orden_tarjetas">Orden de tarjetas</Label>
              <Select
                value={ordenTarjetas}
                onValueChange={(value) => setValue('orden_tarjetas', value as OrdenKanban)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccionar orden" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ORDEN_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="tamano_tarjeta">Tamano de tarjetas</Label>
              <Select
                value={tamanoTarjeta}
                onValueChange={(value) => setValue('tamano_tarjeta', value as 'compacto' | 'normal' | 'expandido')}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccionar tamano" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TAMANO_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-3 pt-2">
            <SwitchWithLabel
              checked={mostrarArchivados}
              onCheckedChange={(checked) => setValue('mostrar_archivados', checked)}
              label="Mostrar archivados"
              description="Incluir tickets/actividades archivadas"
            />
            <SwitchWithLabel
              checked={esCompartido}
              onCheckedChange={(checked) => setValue('es_compartido', checked)}
              label="Compartir tablero"
              description="Otros usuarios podran ver este tablero"
            />
          </div>

          <DialogFooter className="pt-4">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? 'Guardando...'
                : tablero
                ? 'Guardar cambios'
                : 'Crear tablero'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
